/* Created by Interactive 63 - https://interactive63.com */
/**
 * The inspector's element section: the layer name and tag, the transform origin grid with
 * custom X and Y fields (as a percentage of the layer's box and in document units), and the
 * editor-visibility and export toggles.
 */
import type { ElementId } from '../../../core/model/types';
import {
  renameElement,
  setElementOrigin,
  toggleElementExcluded,
  toggleElementHidden,
} from '../../store/actions';
import type { Store } from '../../store/store';
import { h } from '../../ui/dom';
import { roundTo } from '../stage/geometry';
import { NumberInput } from './inputs/number-input';

const STEPS = [0, 50, 100];
const ROW_NAMES = ['top', 'middle', 'bottom'];
const COLUMN_NAMES = ['left', 'centre', 'right'];

export class ElementSection {
  readonly el: HTMLElement;
  private readonly name: HTMLInputElement;
  private readonly originX: NumberInput;
  private readonly originY: NumberInput;
  private readonly originXUnits: NumberInput;
  private readonly originYUnits: NumberInput;
  private readonly grid: HTMLButtonElement[] = [];
  private readonly hidden: HTMLButtonElement;
  private readonly excluded: HTMLButtonElement;

  /**
   * Builds the section for one element. `showOrigin` is false for elements that cannot
   * transform. `size` reports the layer's box in document units, for the pixel fields.
   */
  constructor(
    private readonly store: Store,
    private readonly id: ElementId,
    showOrigin: boolean,
    private readonly size: () => { width: number; height: number } | null = () => null,
  ) {
    const meta = store.getState().project.elements[id];
    this.name = h('input', {
      class: 'element-name',
      'aria-label': 'Element name',
      value: meta?.label ?? '',
    });
    this.name.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.name.blur();
      else if (event.key === 'Escape') {
        this.name.value = this.store.getState().project.elements[id]?.label ?? '';
        this.name.blur();
      }
    });
    this.name.addEventListener('blur', () => {
      renameElement(this.store, id, this.name.value);
      this.name.value = this.store.getState().project.elements[id]?.label ?? '';
    });

    const scrubKey = `scrub:origin:${id}`;
    this.originX = new NumberInput({
      label: 'X',
      unit: '%',
      step: 1,
      min: -500,
      max: 500,
      scrubKey,
      onChange: (x, key) => {
        this.setOrigin({ x }, key);
      },
    });
    this.originY = new NumberInput({
      label: 'Y',
      unit: '%',
      step: 1,
      min: -500,
      max: 500,
      scrubKey,
      onChange: (y, key) => {
        this.setOrigin({ y }, key);
      },
    });

    this.originXUnits = new NumberInput({
      label: 'X',
      ariaLabel: 'Origin X in pixels',
      unit: 'px',
      step: 0.1,
      min: -100000,
      max: 100000,
      scrubKey: `${scrubKey}:px`,
      onChange: (px, key) => {
        const width = this.size()?.width;
        if (width) this.setOrigin({ x: (px / width) * 100 }, key);
      },
    });
    this.originYUnits = new NumberInput({
      label: 'Y',
      ariaLabel: 'Origin Y in pixels',
      unit: 'px',
      step: 0.1,
      min: -100000,
      max: 100000,
      scrubKey: `${scrubKey}:px`,
      onChange: (px, key) => {
        const height = this.size()?.height;
        if (height) this.setOrigin({ y: (px / height) * 100 }, key);
      },
    });

    const grid = h('div', {
      class: 'origin-grid',
      role: 'group',
      'aria-label': 'Transform origin',
    });
    STEPS.forEach((y, row) => {
      STEPS.forEach((x, column) => {
        const button = h('button', {
          type: 'button',
          class: 'origin-cell',
          'aria-label': `Origin ${ROW_NAMES[row]} ${COLUMN_NAMES[column]}`,
          title: `Origin ${ROW_NAMES[row]} ${COLUMN_NAMES[column]}`,
          'data-x': x,
          'data-y': y,
        });
        button.addEventListener('click', () => {
          this.setOrigin({ x, y });
        });
        this.grid.push(button);
        grid.append(button);
      });
    });

    this.hidden = h('button', { type: 'button', 'aria-pressed': 'false' }, 'Hide in editor');
    this.hidden.addEventListener('click', () => {
      toggleElementHidden(this.store, id);
    });
    this.excluded = h('button', { type: 'button', 'aria-pressed': 'false' }, 'Exclude from export');
    this.excluded.addEventListener('click', () => {
      toggleElementExcluded(this.store, id);
    });

    this.el = h(
      'section',
      { class: 'inspector-section element-section' },
      h(
        'div',
        { class: 'element-heading' },
        this.name,
        h('span', { class: 'tag-badge' }, meta?.tag ?? ''),
      ),
      showOrigin
        ? h(
            'div',
            { class: 'origin-section' },
            h('div', { class: 'origin-title' }, 'Transform origin'),
            h(
              'div',
              { class: 'origin-controls' },
              grid,
              h(
                'div',
                { class: 'origin-fields' },
                this.originX.el,
                this.originY.el,
                this.originXUnits.el,
                this.originYUnits.el,
              ),
            ),
          )
        : null,
      h('div', { class: 'element-toggles' }, this.hidden, this.excluded),
    );
    this.refresh();
  }

  /** Updates the fields from the store, leaving a field alone while it is being edited. */
  refresh(): void {
    const meta = this.store.getState().project.elements[this.id];
    if (!meta) return;
    if (document.activeElement !== this.name) this.name.value = meta.label;
    this.originX.setValue(meta.origin.x);
    this.originY.setValue(meta.origin.y);
    const size = this.size();
    this.originXUnits.setValue(size ? roundTo((meta.origin.x / 100) * size.width, 2) : null);
    this.originYUnits.setValue(size ? roundTo((meta.origin.y / 100) * size.height, 2) : null);
    for (const cell of this.grid) {
      const active =
        Number(cell.dataset['x']) === meta.origin.x && Number(cell.dataset['y']) === meta.origin.y;
      cell.classList.toggle('is-active', active);
      cell.setAttribute('aria-pressed', String(active));
    }
    this.hidden.setAttribute('aria-pressed', String(meta.hiddenInEditor));
    this.excluded.setAttribute('aria-pressed', String(meta.excludeFromExport));
  }

  /** Applies part of a new origin to the element. */
  private setOrigin(patch: { x?: number; y?: number }, key?: string): void {
    const current = this.store.getState().project.elements[this.id]?.origin;
    if (!current) return;
    setElementOrigin(this.store, this.id, { ...current, ...patch }, key);
  }
}
